import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import {StaticImageData} from 'next/image';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import DisplayImage from '@/components/Ui-components/display-image';
import parse from 'html-react-parser';
import {cdnImage} from '@/util/cdnImage';
import {colors, borderRadius, fontSize, fontWeight, lineHeight, shadows, spacing} from '@/styles/tokens';
import {ICON_TILE_ICON_SIZE, iconTileImageStyle, iconTileSx} from '@/styles/iconTile';
import Eyebrow from '@/components/Common/Eyebrow';

const listCheckIcon = cdnImage('web3/Web3-Best-ListCheck.svg');

export interface IndustrySolutionCard {
    /** Omit to render the card without an icon tile. */
    icon?: string | StaticImageData;
    category: string;
    heading: string;
    description: string;
    tags: string[];
    bullets: string[];
}

export interface IndustrySolutionsProps {
    eyebrow?: string;
    heading: string;
    subheading: string;
    cards: IndustrySolutionCard[];
    /**
     * 'default' — pale icon tile, category kicker above a marketing heading, tag chips.
     * 'compact' — navy icon tile, the category alone as the card title, no chips.
     */
    variant?: 'default' | 'compact';
}

const cardSx = {
    bgcolor: colors.background.default,
    borderRadius: borderRadius.lg,
    border: `1px solid ${colors.neutral.borderLight}`,
    boxShadow: shadows.awardCardElevated,
    p: spacing[24],
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing[16],
    height: '100%',
    transition: 'transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
        borderColor: colors.primary.deep,
        boxShadow: shadows.lg,
        transform: 'scale(1.02)'
    }
} as const;

/** Navy tile with a knocked-out white icon — the 'compact' variant's header. */
const iconBgDarkSx = iconTileSx;

/** The category doubles as the card title in the 'compact' variant. */
const compactTitleSx = {
    fontSize: fontSize[18],
    fontWeight: fontWeight.bold,
    color: colors.text.primary,
    lineHeight: lineHeight[28]
} as const;

const iconBgSx = iconTileSx;

const categorySx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.medium,
    color: colors.primary.main,
    lineHeight: lineHeight[24]
} as const;

const cardHeadingSx = {
    fontSize: fontSize[18],
    fontWeight: fontWeight.bold,
    color: colors.text.primary
} as const;

const descriptionSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    color: colors.text.primary,
    lineHeight: lineHeight[24]
} as const;

const tagSx = {
    bgcolor: colors.background.tag,
    borderRadius: borderRadius.full,
    px: spacing[16],
    py: spacing[8],
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0
} as const;

const tagTextSx = {
    fontSize: fontSize[14],
    fontWeight: fontWeight.medium,
    color: colors.primary.link,
    whiteSpace: 'nowrap' as const,
    lineHeight: lineHeight[20]
} as const;

const bulletTextSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.medium,
    color: colors.text.primary,
    lineHeight: lineHeight[28]
} as const;

const cardHeaderGroupSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing[4]
} as const;

const cardBodyGroupSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing[12]
} as const;

const tagsWrapSx = {
    display: 'flex',
    mt: 1,
    mb: 0.5,
    flexWrap: 'wrap' as const,
    gap: spacing[12]
} as const;

const bulletsWrapSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    gap: spacing[8]
} as const;

const bulletItemSx = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: spacing[12]
} as const;

const bulletIconWrapSx = {
    flexShrink: 0,
    width: 14,
    height: 14,
    position: 'relative' as const,
    mt: spacing[8]
} as const;

const IndustrySolutions = ({eyebrow, heading, subheading, cards, variant = 'default'}: IndustrySolutionsProps) => {
    const isCompact = variant === 'compact';

    return (
        <Box>
            <MUIContainer>
                <Box textAlign="center" maxWidth={1024} margin="0 auto" mb={5}>
                    {eyebrow && <Eyebrow text={eyebrow} />}
                    <Typography variant="h1" component="h2" mb={3} fontSize={{xs: 35, sm: 48}} className="font-headers">
                        {parse(heading)}
                    </Typography>
                    <Typography className="font-regular-text">{subheading}</Typography>
                </Box>

                <Grid container spacing={3}>
                    {cards.map((card, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            <Box sx={cardSx}>
                                {card.icon && (
                                    <Box sx={isCompact ? iconBgDarkSx : iconBgSx}>
                                        <DisplayImage
                                            src={card.icon}
                                            alt={card.category}
                                            width={ICON_TILE_ICON_SIZE}
                                            height={ICON_TILE_ICON_SIZE}
                                            style={iconTileImageStyle}
                                        />
                                    </Box>
                                )}

                                <Box sx={cardHeaderGroupSx}>
                                    {isCompact ? (
                                        <Typography component="h3" className="font-headers" sx={compactTitleSx}>
                                            {card.category}
                                        </Typography>
                                    ) : (
                                        <>
                                            <Typography className="font-regular-text" sx={categorySx}>
                                                {card.category}
                                            </Typography>
                                            <Typography component="h3" className="font-headers" sx={cardHeadingSx}>
                                                {card.heading}
                                            </Typography>
                                        </>
                                    )}
                                </Box>

                                <Box sx={cardBodyGroupSx}>
                                    <Typography className="font-regular-text" sx={descriptionSx}>
                                        {parse(card.description)}
                                    </Typography>
                                    {!isCompact && card.tags.length > 0 && (
                                        <Box sx={tagsWrapSx}>
                                            {card.tags.map((tag) => (
                                                <Box key={tag} sx={tagSx}>
                                                    <Typography className="font-headers" sx={tagTextSx}>
                                                        {tag}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>

                                {card.bullets.length > 0 && (
                                    <Box sx={bulletsWrapSx}>
                                        {card.bullets.map((bullet, i) => (
                                            <Box key={i} sx={bulletItemSx}>
                                                <Box sx={bulletIconWrapSx}>
                                                    <DisplayImage src={listCheckIcon} alt="Included" fill />
                                                </Box>
                                                <Typography className="font-regular-text" sx={bulletTextSx}>
                                                    {parse(bullet)}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </MUIContainer>
        </Box>
    );
};

export default memo(IndustrySolutions);
